import React, { useEffect, useState, startTransition } from 'react';

import { ZipCodeCity } from '@apis/zipCodeCityApis';
import { TextInput } from '@components/admin/Forms';
import { ManagementTextInput } from '@components/Forms/ManagementTextInput';
import { SemiAutoCompleteOption } from '@components/TextBox';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { contactStrings } from '@data/contacts';
import { usePrefAndCityByZipcode } from '@hooks/queries/usePrefAndCityByZipcode';
import { useZipCodeCityByCity } from '@hooks/queries/useZipCodeCityByCity';
import { useZipCodeCityByZipCode } from '@hooks/queries/useZipCodeCityByZipCode';
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { useIsAdmin } from '@hooks/useIsAdmin';
import { jpContacts, ContactType } from '@models/next/Contact';
import { makeMuiStyles, useCommonStyles, useIsMobile } from '@styles';
import { joinKeyPath } from '@utils/forms';
import { createStrings } from '@utils/local';
import { isValidJapaneseZipcode, sanitizeZipCode } from '@utils/sanitize';
import clsx from 'clsx';
import { useField, useFormikContext } from 'formik';
import useDebounce from 'react-use/lib/useDebounce';

const strings = createStrings({
  ja: {
    cnCompanyNamePlaceholder: '例：东方物产有限公司',
    jpCompanyNamePlaceholder: '例：株式会社　ABC',
    cnEnglishNamePlaceholder: '例：Dongfang Wuchan Co., Ltd.',
    jpEnglishNamePlaceholder: '例：ABC Co., Ltd.',
    cnZipCodePlaceholder: '6桁の中国の郵便番号を入力してください　例：523000',
    jpZipCodePlaceholder: '日本の郵便番号を入力してください',
    cnAddress1CnPlaceholder: '都市名（中国語）',
    cnAddress1Placeholder: '都市名（英字名）',
    jpAddress1Placeholder: '都道府県・市区町村・番地を入力してください',
    cnAddress2Placeholder: '中国の街道・建物名・号室などを入力してください　例：No.○, ○○ Road',
    jpAddress2Placeholder: '建物名・号室を入力してください',
    cnPrincipalNamePlaceholder: '例：南さん',
    jpPrincipalNamePlaceholder: '例：荷物太郎',
    cnPhonePlaceholder: '例：138 xxxx xxxx',
    jpPhonePlaceholder: '例：03-1234-2222',
    emailPlaceholder: '例：d2dtaro@d2dmail.com',
    cnSnsIdPlaceholder: '例：wechat ID: d2dtaro',
    jpSnsIdPlaceholder: '例：LINE ID: d2dtaro',
    cnAddressInputTips:
      '＊中国国内の都市名(英字名)と郵便番号のいずれか一つを入力して、現れた選択肢中から一つを選んでください',
    pleaseInputValidZipCode: '有効な郵便番号を入力してください',
    pleaseInputValidCity: '有効な都市名を入力してください',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    flex: 1,
  },
  cnAddressInputContainer: {
    display: 'grid',
    columnGap: '1rem',
    gridTemplateColumns: '1fr 1fr 1fr',
    '&$omitAddress1CN': {
      gridTemplateColumns: '2fr 1fr',
    },
  },
  textInputContainer2: {
    ...common.flexColumn,
    paddingBottom: 11,
    paddingTop: '0.58rem',
    flex: 1,
    [theme.breakpoints.down('sm')]: {
      marginTop: 'calc(2.375rem + 5px)',
    },
  },
  textInput: {
    borderColor: '#DCDEE0 !important',
    '&:focus-within': {
      borderColor: `${theme.palette.primary.main} !important`,
    },
  },
  inputTips: {
    ...common.exSmallerText,
    color: theme.palette.grey['500'],
    marginLeft: 'calc(1rem + 170px)',
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
    },
  },
  omitAddress1CN: {},
}));

export interface ZipCodeChangeProps {
  zipCodeFieldName: string;
  addressFieldName: string;
}

export const HandleJPZipcodeChange: React.FC<ZipCodeChangeProps> = (props) => {
  const { zipCodeFieldName, addressFieldName } = props;

  const [field, meta] = useField(zipCodeFieldName);

  const formik = useFormikContext();

  const [zipCode, setZipCode] = useState<string>('');

  const prefCity = usePrefAndCityByZipcode({ zipcode: sanitizeZipCode(zipCode) });

  useDebounce(
    () => {
      if (field.value) {
        if (isValidJapaneseZipcode(sanitizeZipCode(field.value))) {
          setZipCode(field.value);
        } else {
          setZipCode('');
        }
      }
    },
    500,
    [field.value],
  );

  useEffect(() => {
    if (prefCity.data?.city && prefCity.data?.pref && field.value !== meta.initialValue) {
      startTransition(() => {
        formik.setFieldValue(
          addressFieldName,
          `${prefCity.data?.pref} ${prefCity.data?.city}`,
          true,
        );
      });
    }
  }, [prefCity.data?.city, prefCity.data?.pref, prefCity.isSuccess, zipCode]);

  return null;
};

export interface HandleCNZipcodeChangeProps {
  zipCodeFieldName: string;
  addressFieldName: string;
  addressCNFieldName: string;
  onZipCodeSemiAutoCompleteOptions?: (options: CNZipCodeSemiAutoCompleteOption[]) => void;
  onAddressSemiAutoCompleteOptions?: (options: CNZipCodeSemiAutoCompleteOption[]) => void;
  onAddressCNSemiAutoCompleteOptions?: (options: CNZipCodeSemiAutoCompleteOption[]) => void;
}

export interface CNZipCodeSemiAutoCompleteOption extends SemiAutoCompleteOption {
  value: {
    zipcode: string;
    address: string;
    address_cn: string;
  };
}

const mapZipCodeCityToCNZipCodeSemiAutoCompleteOptionValue = (
  v: ZipCodeCity,
): CNZipCodeSemiAutoCompleteOption['value'] => {
  return {
    zipcode: v.zipcode,
    address: `${v.province}, ${v.city}`,
    address_cn: `${v.province_zh}, ${v.city_zh}`,
  };
};

const SEMI_AUTO_COMPELETION_LIMIT = 100;

export const HandleCNZipcodeChange: React.FC<HandleCNZipcodeChangeProps> = (props) => {
  const {
    zipCodeFieldName,
    addressFieldName,
    addressCNFieldName,
    onZipCodeSemiAutoCompleteOptions,
    onAddressSemiAutoCompleteOptions,
    onAddressCNSemiAutoCompleteOptions,
  } = props;

  const [zipCodeField] = useField(zipCodeFieldName);

  const [addressField] = useField(addressFieldName);

  const [addressCNField] = useField(addressCNFieldName);

  const formik = useFormikContext();

  const [zipCode, setZipCode] = useState<string>();

  const address = useDebouncedValue(() => addressField.value, 250, [addressField.value]);

  const addressCN = useDebouncedValue(() => addressCNField.value, 250, [addressCNField.value]);

  const cityByZipCode = useZipCodeCityByZipCode({ zipcode: zipCode });

  const zipCodeByCity = useZipCodeCityByCity({ city: address });

  const zipCodeByCityCN = useZipCodeCityByCity({ city: addressCN, enable_zh: true });

  // zip code
  useDebounce(
    () => {
      if (zipCodeField.value && sanitizeZipCode(zipCodeField.value).length >= 3) {
        setZipCode(zipCodeField.value);
      } else {
        setZipCode('');
        onZipCodeSemiAutoCompleteOptions?.([]);
      }
    },
    500,
    [zipCodeField.value],
  );

  useEffect(() => {
    if (cityByZipCode.data) {
      startTransition(() => {
        onZipCodeSemiAutoCompleteOptions?.(
          cityByZipCode.data?.slice(0, SEMI_AUTO_COMPELETION_LIMIT).map((item) => {
            return {
              label: `${item.zipcode} ${item.province}, ${item.city}`,
              value: mapZipCodeCityToCNZipCodeSemiAutoCompleteOptionValue(item),
            };
          }) || [],
        );
        if (cityByZipCode.data?.length === 1 && addressField.value === '') {
          formik.setFieldValue(zipCodeFieldName, `${cityByZipCode.data?.[0].zipcode}`, true);
          formik.setFieldValue(
            addressFieldName,
            `${cityByZipCode.data?.[0].province}, ${cityByZipCode.data?.[0].city}`,
            true,
          );
        }
      });
    } else if (cityByZipCode.isSuccess) {
      startTransition(() => {
        onZipCodeSemiAutoCompleteOptions?.([]);
      });
    }
  }, [cityByZipCode.data, cityByZipCode.isSuccess]);

  useEffect(() => {
    if (zipCodeByCity.data) {
      startTransition(() => {
        onAddressSemiAutoCompleteOptions?.(
          zipCodeByCity.data?.slice(0, SEMI_AUTO_COMPELETION_LIMIT).map((item) => {
            return {
              label: `${item.province}, ${item.city} ${item.zipcode}`,
              value: mapZipCodeCityToCNZipCodeSemiAutoCompleteOptionValue(item),
            };
          }) || [],
        );
        if (zipCodeByCity.data?.length === 1 && addressField.value === '') {
          formik.setFieldValue(zipCodeFieldName, `${zipCodeByCity.data?.[0].zipcode}`, true);
          formik.setFieldValue(
            addressFieldName,
            `${zipCodeByCity.data?.[0].province}, ${zipCodeByCity.data?.[0].city}`,
            true,
          );
        }
      });
    } else if (zipCodeByCity.isSuccess) {
      startTransition(() => {
        onAddressSemiAutoCompleteOptions?.([]);
      });
    }
  }, [zipCodeByCity.data, zipCodeByCity.isSuccess]);

  useEffect(() => {
    if (zipCodeByCityCN.data) {
      startTransition(() => {
        onAddressCNSemiAutoCompleteOptions?.(
          zipCodeByCityCN.data?.slice(0, SEMI_AUTO_COMPELETION_LIMIT).map((item) => ({
            label: `${item.province_zh}, ${item.city_zh} ${item.province}, ${item.city} ${item.zipcode}`,
            value: mapZipCodeCityToCNZipCodeSemiAutoCompleteOptionValue(item),
          })) || [],
        );

        // TODO
      });
    } else if (zipCodeByCityCN.isSuccess) {
      startTransition(() => {});
    }
  }, [zipCodeByCityCN.data, zipCodeByCityCN.isSuccess]);

  return null;
};

export interface ContactFormProps {
  name?: string;
  withPreferredArrivalDate?: boolean;
  className?: string;
  disabled?: boolean;
  zipcodeRequired?: boolean;
  contactType: ContactType;
}

export const ContactForm: React.FC<ContactFormProps> = (props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const {
    name,
    withPreferredArrivalDate = false,
    className,
    disabled,
    zipcodeRequired,
    contactType,
  } = props;

  const [zipcodeChoices, setZipCodeChoices] = useState<CNZipCodeSemiAutoCompleteOption[]>([]);
  const [addressChoices, setAddressChoices] = useState<CNZipCodeSemiAutoCompleteOption[]>([]);
  const [addressCNChoices, setAddressCNChoices] = useState<CNZipCodeSemiAutoCompleteOption[]>([]);
  const formik = useFormikContext();
  const isMobile = useIsMobile();
  const isAdmin = useIsAdmin();

  const isJPContact = jpContacts.includes(contactType);
  const isShipper = contactType === ContactType.enum.shipper;

  const fieldName = (f: string) => {
    return joinKeyPath(name, f);
  };

  const onCNSemiAutoCompleteOptionSelect = async (option: CNZipCodeSemiAutoCompleteOption) => {
    const { zipcode, address, address_cn } = option.value;
    // Using await here can avoid validation with outdated values
    await formik.setFieldValue(fieldName('zip_code'), zipcode, true);
    await formik.setFieldValue(fieldName('address1'), address, true);
    await formik.setFieldValue(fieldName('address1_cn'), address_cn, true);
  };

  return (
    <div data-testid="ContactForm" className={clsx(classes.container, className)}>
      {isJPContact && !disabled && (
        <HandleJPZipcodeChange
          zipCodeFieldName={fieldName('zip_code')}
          addressFieldName={fieldName('address1')}
        />
      )}
      {!isJPContact && !disabled && (
        <HandleCNZipcodeChange
          zipCodeFieldName={fieldName('zip_code')}
          addressFieldName={fieldName('address1')}
          addressCNFieldName={fieldName('address1_cn')}
          onZipCodeSemiAutoCompleteOptions={setZipCodeChoices}
          onAddressSemiAutoCompleteOptions={setAddressChoices}
          onAddressCNSemiAutoCompleteOptions={setAddressCNChoices}
        />
      )}
      <ManagementTextInput
        name={fieldName('company_name')}
        label={contactStrings.company_name}
        labelDestructive={isMobile}
        required={isJPContact}
        placeholder={
          isJPContact ? strings.jpCompanyNamePlaceholder : strings.cnCompanyNamePlaceholder
        }
        disabled={disabled}
      />

      <ManagementTextInput
        name={fieldName('english_name')}
        label={contactStrings.english_name}
        labelDestructive={isMobile}
        required
        placeholder={
          isJPContact ? strings.jpEnglishNamePlaceholder : strings.cnEnglishNamePlaceholder
        }
        disabled={disabled}
      />

      {isShipper && (
        <ManagementTextInput
          name={fieldName('social_credit_id')}
          label={contactStrings.social_credit_id}
          labelDestructive={isMobile}
          placeholder={'XX-XXXXXX-XXXXXXXX-X'}
          disabled={disabled}
        />
      )}

      {isJPContact ? (
        <>
          <ManagementTextInput
            name={fieldName('zip_code')}
            label={contactStrings.zip_code}
            labelDestructive={isMobile}
            required={zipcodeRequired}
            placeholder={strings.jpZipCodePlaceholder}
            disabled={disabled}
          />
          <ManagementTextInput
            name={fieldName('address1')}
            label={contactStrings.address1}
            labelDestructive={isMobile}
            required
            placeholder={strings.jpAddress1Placeholder}
            disabled={disabled}
          />
        </>
      ) : (
        <>
          <div
            className={clsx(classes.cnAddressInputContainer, !isAdmin && classes.omitAddress1CN)}
          >
            {isAdmin && (
              <>
                <ManagementTextInput
                  name={fieldName('address1_cn')}
                  label={contactStrings.city}
                  labelDestructive={isMobile}
                  required
                  placeholder={strings.cnAddress1CnPlaceholder}
                  disabled={disabled}
                  autoCompleteWidth={350}
                  withSemiAutoComplete
                  semiAutoCompleteOptions={addressCNChoices}
                  onSemiAutoCompleteOptionSelect={onCNSemiAutoCompleteOptionSelect}
                />

                <ManagementTextInput
                  noLabel
                  name={fieldName('address1')}
                  labelDestructive={isMobile}
                  required
                  placeholder={strings.cnAddress1Placeholder}
                  disabled={disabled}
                  withSemiAutoComplete
                  semiAutoCompleteOptions={addressChoices}
                  onSemiAutoCompleteOptionSelect={onCNSemiAutoCompleteOptionSelect}
                />
              </>
            )}

            {!isAdmin && (
              <ManagementTextInput
                label={contactStrings.city}
                name={fieldName('address1')}
                labelDestructive={isMobile}
                required
                placeholder={strings.cnAddress1Placeholder}
                disabled={disabled}
                withSemiAutoComplete
                semiAutoCompleteOptions={addressChoices}
                onSemiAutoCompleteOptionSelect={onCNSemiAutoCompleteOptionSelect}
              />
            )}

            <div className={classes.textInputContainer2}>
              <TextInput
                noLabel
                name={fieldName('zip_code')}
                placeholder={contactStrings.zip_code}
                disabled={disabled}
                classes={{
                  textInput: classes.textInput,
                }}
                withSemiAutoComplete
                semiAutoCompleteOptions={zipcodeChoices}
                onSemiAutoCompleteOptionSelect={onCNSemiAutoCompleteOptionSelect}
              />
            </div>
          </div>
          <div className={classes.inputTips}>{strings.cnAddressInputTips}</div>
        </>
      )}
      <ManagementTextInput
        name={fieldName('address2')}
        label={isJPContact ? contactStrings.address2 : contactStrings.address}
        labelDestructive={isMobile}
        placeholder={isJPContact ? strings.jpAddress2Placeholder : strings.cnAddress2Placeholder}
        disabled={disabled}
      />
      <ManagementTextInput
        name={fieldName('principal_name')}
        label={contactStrings.principal_name}
        labelDestructive={isMobile}
        required
        placeholder={
          isJPContact ? strings.jpPrincipalNamePlaceholder : strings.cnPrincipalNamePlaceholder
        }
        disabled={disabled}
      />
      <ManagementTextInput
        name={fieldName('phone')}
        label={contactStrings.phone}
        labelDestructive={isMobile}
        required
        placeholder={isJPContact ? strings.jpPhonePlaceholder : strings.cnPhonePlaceholder}
        disabled={disabled}
      />
      <ManagementTextInput
        name={fieldName('email')}
        label={contactStrings.email}
        labelDestructive={isMobile}
        placeholder={strings.emailPlaceholder}
        disabled={disabled}
      />
      <ManagementTextInput
        name={fieldName('sns_id')}
        label={contactStrings.sns_id}
        labelDestructive={isMobile}
        placeholder={isJPContact ? strings.jpSnsIdPlaceholder : strings.cnSnsIdPlaceholder}
        disabled={disabled}
      />
      {withPreferredArrivalDate && (
        <ManagementTextInput
          name={fieldName('preferred_arrival_date')}
          label={bookingInquiryStrings.preferredArrivalDate}
          labelDestructive={isMobile}
          placeholder={bookingInquiryStrings.preferredArrivalDatePlaceholder}
          disabled={disabled}
        />
      )}
    </div>
  );
};
